import { useState } from "react";
import { Alert, App, Button, Card, Checkbox, Input, List, Space, Tag, Typography } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import { collection, addDoc } from "firebase/firestore";
import { db } from "../lib/firebase";
import ChatPanel from "../components/ChatPanel";
import { streamVendorIntake, submitVendorIntake } from "../lib/api";
import { APP_BRAND_NAME } from "../lib/brand";

const PARTICIPATION_AGREEMENT_URL = "/agreements/nc-ai-accelerator-participation-agreement.pdf";

const sessionId = crypto.randomUUID();

const DIT_PRIMARY = "#092940";
const DIT_LOGO_URL =
  "https://files.nc.gov/dit/dit-logo_0.png?VersionId=hXzat98jbejrN24AhcEkTkX4mhZaJBAO";
const DIT_LOGO_ALT = "NC Department of Information Technology logo";

export default function VendorIntake() {
  // OAIP-68: vendor flow now mirrors the agency Submit pattern. The AI fills
  // a draft profile (`draftReady`); the user reviews/edits it and clicks
  // **Confirm Profile** (`profileConfirmed`); only then can they Review &
  // Sign the participation agreement.
  const [intakeId, setIntakeId] = useState<string | null>(null);
  const [draftReady, setDraftReady] = useState(false);
  const [profileConfirmed, setProfileConfirmed] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [capabilityTags, setCapabilityTags] = useState<string[]>([]);
  const [vendorProfile, setVendorProfile] = useState("");
  // OAIP-69: in-browser signature canvas removed. Vendors download the
  // official PDF, review it offline, and confirm they're ready to sign.
  // The OAIP team handles real signatures out-of-band.
  const [reviewedAgreement, setReviewedAgreement] = useState(false);
  const [submittingReadyToSign, setSubmittingReadyToSign] = useState(false);
  const [readyToSign, setReadyToSign] = useState(false);
  const { message } = App.useApp();

  return (
    <Space direction="vertical" size="large" style={{ width: "100%", fontFamily: "'Source Sans Pro', system-ui, sans-serif" }}>
      <div
        style={{
          background: DIT_PRIMARY,
          color: "#FFFFFF",
          padding: "20px 24px",
          borderRadius: 8,
          display: "flex",
          alignItems: "center",
          gap: 16,
        }}
      >
        <img
          src={DIT_LOGO_URL}
          alt={DIT_LOGO_ALT}
          style={{ height: 56, width: "auto", background: "#FFFFFF", padding: 6, borderRadius: 4 }}
        />
        <div>
          <Typography.Title level={2} style={{ margin: 0, color: "#FFFFFF" }}>
            Vendor Intake
          </Typography.Title>
          <Typography.Text style={{ color: "#EEF7FF" }}>
            Join the AI Accelerator vendor network. {APP_BRAND_NAME} learns your capabilities and prepares your matching profile.
          </Typography.Text>
        </div>
      </div>

      {!draftReady ? (
        <ChatPanel
          title="Tell us about your company"
          description={`${APP_BRAND_NAME} will research your company, gather your capabilities, and prepare your vendor profile.`}
          openingPrompt={`Hi! I'm ${APP_BRAND_NAME}, and I'm here to help you join the NC AI Accelerator vendor network. To get started, what is your company name?`}
          placeholder="Share your company name to get started..."
          showUndo={false}
          showDeleteLastProject={false}
          onSend={async (message, handlers, _history, files) => {
            const attachments = (files || [])
              .filter((f) => f.content)
              .map((f) => ({ mimeType: f.type, data: f.content, name: f.file.name }));
            const { meta } = await streamVendorIntake(
              {
                message,
                sessionId,
                ...(attachments.length > 0 ? { attachments } : {}),
              },
              { onDelta: handlers.onChunk, onThought: handlers.onThought, onMeta: handlers.onMeta }
            );
            if (meta && typeof meta === "object") {
              if (typeof meta.intakeId === "string") {
                setIntakeId(meta.intakeId);
              }
              if (meta.draftProfile && typeof meta.draftProfile === "object") {
                setDraftReady(true);
                setCapabilityTags(meta.draftProfile.capabilityTags || []);
                setVendorProfile(meta.draftProfile.vendorProfile || "");
              }
            }
            return undefined;
          }}
        />
      ) : (
        <Card>
          <Space direction="vertical" size="middle" style={{ width: "100%" }}>
            <div>
              <Typography.Title level={4} style={{ marginBottom: 0 }}>
                Your Vendor Profile
              </Typography.Title>
              <Typography.Text type="secondary">
                {profileConfirmed
                  ? "Profile confirmed — continue to the participation agreement below."
                  : "Edit anything that needs adjusting, then click Confirm Profile."}
              </Typography.Text>
            </div>
            <Input.TextArea
              value={vendorProfile}
              disabled={profileConfirmed || confirming}
              onChange={(e) => setVendorProfile(e.target.value)}
              autoSize={{ minRows: 4 }}
            />
            {capabilityTags.length > 0 && (
              <Space wrap>
                <Typography.Text type="secondary">Capability Tags:</Typography.Text>
                {capabilityTags.map((tag) => (
                  <Tag key={tag}>{tag}</Tag>
                ))}
              </Space>
            )}
            {confirmError && (
              <Alert
                type="error"
                showIcon
                closable
                message={confirmError}
                onClose={() => setConfirmError(null)}
              />
            )}
            <Space style={{ width: "100%", justifyContent: "flex-end" }}>
              <Button
                type="primary"
                loading={confirming}
                disabled={profileConfirmed || !intakeId || !vendorProfile.trim()}
                onClick={async () => {
                  if (!intakeId) return;
                  setConfirming(true);
                  setConfirmError(null);
                  try {
                    await submitVendorIntake({
                      intakeId,
                      vendorProfile: vendorProfile.trim(),
                      capabilityTags,
                    });
                    setProfileConfirmed(true);
                    void message.success("Profile confirmed — continue to the agreement to finish.");
                  } catch (err) {
                    setConfirmError(
                      err instanceof Error
                        ? err.message
                        : "Couldn't save the profile. Please try again."
                    );
                  } finally {
                    setConfirming(false);
                  }
                }}
              >
                {profileConfirmed ? "Profile Confirmed ✓" : confirming ? "Saving…" : "Confirm Profile"}
              </Button>
            </Space>
          </Space>
        </Card>
      )}

      {!readyToSign ? (
        <Card>
          <Space direction="vertical" size="middle" style={{ width: "100%" }}>
            <div>
              <Typography.Title level={4} style={{ marginBottom: 0 }}>
                Participation Agreement
              </Typography.Title>
              <Typography.Text type="secondary">
                Final step: download the official NC AI Accelerator participation agreement, review it offline, and
                confirm you're ready to sign. The OAIP team will reach out to coordinate the actual signature.
              </Typography.Text>
            </div>

            <Card size="small">
              <Space direction="vertical" size="small" style={{ width: "100%" }}>
                <Typography.Text strong>What this agreement covers</Typography.Text>
                <List
                  dataSource={[
                    "Allows your company profile to be shared with state agencies for project matching",
                    "Requires compliance with state procurement processes if selected for projects",
                    "Mandates adherence to security, privacy, and data handling requirements",
                    "Does not guarantee project assignments or exclusive partnerships",
                  ]}
                  renderItem={(item) => <List.Item>{item}</List.Item>}
                />
              </Space>
            </Card>

            <Space direction="vertical" size="small" style={{ width: "100%" }}>
              <Button
                type="default"
                icon={<DownloadOutlined />}
                href={PARTICIPATION_AGREEMENT_URL}
                target="_blank"
                rel="noopener noreferrer"
                size="large"
              >
                Download Agreement (PDF)
              </Button>
              <Typography.Text type="secondary">
                Review the document with your team. When you're ready to proceed, check the box below and click
                <strong> I'm Ready to Sign</strong>. We'll follow up to coordinate the signature.
              </Typography.Text>
            </Space>

            <Checkbox
              checked={reviewedAgreement}
              onChange={(e) => setReviewedAgreement(e.target.checked)}
              disabled={!profileConfirmed || submittingReadyToSign}
            >
              I have downloaded and reviewed the participation agreement and am ready to sign when contacted by the
              OAIP team.
            </Checkbox>

            <Button
              type="primary"
              loading={submittingReadyToSign}
              disabled={!profileConfirmed || !reviewedAgreement || submittingReadyToSign}
              onClick={async () => {
                setSubmittingReadyToSign(true);
                try {
                  await addDoc(collection(db, "vendorsPublic"), {
                    sessionId,
                    profile: vendorProfile,
                    capabilityTags,
                    agreementReviewedAt: new Date().toISOString(),
                    status: "ready-to-sign",
                  });
                  setReadyToSign(true);
                } catch (error) {
                  console.error("Error confirming ready-to-sign:", error);
                  message.error("Couldn't record your confirmation. Please try again.");
                } finally {
                  setSubmittingReadyToSign(false);
                }
              }}
            >
              {submittingReadyToSign ? "Submitting…" : "I'm Ready to Sign"}
            </Button>
          </Space>
        </Card>
      ) : (
        <Card>
          <Space direction="vertical" size="small" style={{ width: "100%" }}>
            <Typography.Title level={4} style={{ marginBottom: 0 }}>
              ✓ You're on the list
            </Typography.Title>
            <Typography.Text type="secondary">
              Thanks — we've recorded that you're ready to sign the participation agreement. The OAIP team will reach
              out to coordinate the signature. Once that's complete, you'll be eligible for project matching
              opportunities and we'll contact you when relevant projects emerge.
            </Typography.Text>
          </Space>
        </Card>
      )}
    </Space>
  );
}
